import { FC } from "react";
import { Controller } from "react-hook-form";
import {
  ArrowRightIcon,
  CheckIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

import { BottomSheet } from "components/BottomSheet";
import { Link } from "components/Link";
import {
  SheetPrimaryButton,
  SheetSecondaryButton,
} from "pages/app/uppdrag/transportSedel/operator/SheetActions";
import { WasteStationSignatureResult } from "api/transportSedel.api";
import { cn } from "helpers/classNames";
import { useCompleteUppdragModal } from "./useCompleteUppdragModal.hook";

interface CompleteUppdragModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: WasteStationSignatureResult[];
}

export const CompleteUppdragModal: FC<CompleteUppdragModalProps> = ({
  isOpen,
  onClose,
  results,
}) => {
  const { form, fields, onSubmit, isSubmitting, isInvoicingEnabled } =
    useCompleteUppdragModal(results, isOpen, onClose);

  const submit = form.handleSubmit(onSubmit);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Slutför uppdrag"
      subtitle={
        results.length === 1
          ? "1 uppdrag att slutföra"
          : `${results.length} uppdrag att slutföra`
      }
      footer={
        <>
          <SheetSecondaryButton onClick={onClose}>Avbryt</SheetSecondaryButton>
          <SheetPrimaryButton
            type="button"
            onClick={submit}
            isLoading={isSubmitting}
            loadingText="Slutför..."
          >
            Slutför
          </SheetPrimaryButton>
        </>
      }
    >
      <ul className="space-y-3">
        {results.map((result, index) => {
          const workedHoursError =
            form.formState.errors.uppdragItems?.[index]?.workedHours?.message;
          const drivingHoursError =
            form.formState.errors.uppdragItems?.[index]?.drivingHours?.message;

          return (
            <li
              key={fields[index]?.id || index}
              className="space-y-4 rounded-2xl border border-gray-100 bg-white p-4"
            >
              {/* Facility + uppdrag link */}
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-gray-50 text-gray-600">
                  <MapPinIcon className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-bold tracking-tight text-gray-900">
                    {result.facility.name}
                  </p>
                  <Link
                    to="/app/uppdrag/$uuid"
                    params={{ uuid: result.uppdragUuid }}
                    className="group mt-0.5 inline-flex items-center gap-1 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-700"
                  >
                    Uppdrag #{result.uppdragId}
                    <ArrowRightIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>

              {/* Worked hours */}
              {isInvoicingEnabled && (
                <div>
                  <label
                    htmlFor={`worked-hours-${index}`}
                    className="mb-1.5 block text-sm font-semibold text-gray-900"
                  >
                    Arbetad tid
                  </label>
                  <div className="relative">
                    <input
                      id={`worked-hours-${index}`}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="0"
                      {...form.register(`uppdragItems.${index}.workedHours`)}
                      className={cn(
                        "h-12 w-full rounded-xl border bg-white pl-3.5 pr-16 text-base text-gray-900 transition focus:outline-none focus:ring-4",
                        workedHoursError
                          ? "border-red-300 focus:border-red-400 focus:ring-red-500/15"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15",
                      )}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-sm font-medium text-gray-400">
                      timmar
                    </span>
                  </div>
                  {!!workedHoursError && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {workedHoursError}
                    </p>
                  )}
                </div>
              )}

              {/* Driving hours */}
              {isInvoicingEnabled && (
                <div>
                  <label
                    htmlFor={`driving-hours-${index}`}
                    className="mb-1.5 block text-sm font-semibold text-gray-900"
                  >
                    Körtid
                  </label>
                  <div className="relative">
                    <input
                      id={`driving-hours-${index}`}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="0"
                      {...form.register(`uppdragItems.${index}.drivingHours`)}
                      className={cn(
                        "h-12 w-full rounded-xl border bg-white pl-3.5 pr-16 text-base text-gray-900 transition focus:outline-none focus:ring-4",
                        drivingHoursError
                          ? "border-red-300 focus:border-red-400 focus:ring-red-500/15"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15",
                      )}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-sm font-medium text-gray-400">
                      timmar
                    </span>
                  </div>
                  {!!drivingHoursError && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {drivingHoursError}
                    </p>
                  )}
                </div>
              )}

              {/* Completed toggle */}
              <Controller
                control={form.control}
                name={`uppdragItems.${index}.isCompleted`}
                render={({ field }) => {
                  const disabled = fields[index]?.isCompletedDisabled;
                  const checked = !!field.value;
                  return (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => field.onChange(!checked)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition",
                        disabled
                          ? "cursor-not-allowed border-gray-100 bg-gray-25"
                          : checked
                            ? "border-gray-300 bg-white active:bg-gray-25"
                            : "border-gray-200 bg-white active:bg-gray-25",
                      )}
                    >
                      <span
                        className={cn(
                          "grid h-6 w-6 flex-none place-items-center rounded-md border-2 transition",
                          checked
                            ? "border-green-600 bg-green-600 text-white"
                            : "border-gray-300 bg-white",
                        )}
                      >
                        {checked && (
                          <CheckIcon className="h-4 w-4" strokeWidth={3} />
                        )}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={cn(
                            "block text-base font-semibold",
                            disabled ? "text-gray-400" : "text-gray-900",
                          )}
                        >
                          Uppdrag slutfört
                        </span>
                        {disabled && (
                          <span className="block text-sm text-gray-400">
                            Kan inte slutföras än
                          </span>
                        )}
                      </span>
                    </button>
                  );
                }}
              />
            </li>
          );
        })}
      </ul>
    </BottomSheet>
  );
};
